import React, { useEffect, useState } from 'react';
import { X, Info } from 'lucide-react';
import billingService, {
  BillingCategory,
  BillingConfig
} from '../../../services/whatsapp/billingService';

interface BillingRatesDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const CATEGORIES: { key: BillingCategory; label: string; hint: string }[] = [
  {
    key: 'marketing',
    label: 'Marketing',
    hint: 'Promotions and offers — the most expensive category'
  },
  { key: 'utility', label: 'Utility', hint: 'Order updates, reminders, alerts' },
  { key: 'authentication', label: 'Authentication', hint: 'One-time passcodes' },
  {
    key: 'service',
    label: 'Service',
    hint: 'Replies to customer-initiated chats — free since Nov 2024'
  },
  {
    key: 'referral_conversion',
    label: 'Referral',
    hint: 'Conversations from click-to-WhatsApp ads'
  }
];

/**
 * Edits the rate card and monthly budget.
 *
 * Rates are per *conversation*, not per message — the single most common way
 * to get this wrong is to enter a per-message price, which would understate
 * spend, so the dialog says it on every field.
 */
const BillingRatesDialog: React.FC<BillingRatesDialogProps> = ({
  open,
  onClose,
  onSaved
}) => {
  const [config, setConfig] = useState<BillingConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setError(null);
    billingService
      .getConfig()
      .then(setConfig)
      .catch((err) => setError(err.message || 'Could not load billing settings'))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const setRate = (category: BillingCategory, value: string) => {
    setConfig((previous) =>
      previous
        ? {
            ...previous,
            rates: { ...previous.rates, [category]: Number(value) || 0 }
          }
        : previous
    );
  };

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    setError(null);
    try {
      await billingService.updateConfig({
        currency: config.currency,
        rates: config.rates,
        monthlyBudget: config.monthlyBudget,
        budgetAlertThreshold: config.budgetAlertThreshold
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Could not save billing settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            Rates &amp; budget
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {loading && <p className="text-xs text-gray-500">Loading…</p>}

          {config && (
            <>
              <p className="flex items-start gap-1.5 rounded-lg bg-gray-50 p-2.5 text-[11px] text-gray-600">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                Enter the price per 24-hour <strong>conversation</strong>, not
                per message. Find your rates in Meta Business Manager under
                WhatsApp Manager → Insights, or on Meta's pricing page for your
                country.
              </p>

              <label className="block">
                <span className="text-xs font-medium text-gray-700">
                  Currency
                </span>
                <input
                  type="text"
                  value={config.currency}
                  maxLength={3}
                  onChange={(event) =>
                    setConfig({
                      ...config,
                      currency: event.target.value.toUpperCase()
                    })
                  }
                  placeholder="INR"
                  className="mt-1 w-24 rounded-lg border-gray-300 text-sm uppercase text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                />
              </label>

              <div className="space-y-2">
                <span className="text-xs font-medium text-gray-700">
                  Rate per conversation
                </span>
                {CATEGORIES.map((category) => (
                  <label key={category.key} className="block">
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs text-gray-900">
                          {category.label}
                        </span>
                        <span className="block text-[10px] text-gray-500">
                          {category.hint}
                        </span>
                      </span>
                      <input
                        type="number"
                        min={0}
                        step="0.0001"
                        value={config.rates[category.key] ?? 0}
                        onChange={(event) =>
                          setRate(category.key, event.target.value)
                        }
                        className="w-28 shrink-0 rounded-lg border-gray-300 text-right text-xs text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                      />
                    </div>
                  </label>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-3">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">
                    Monthly budget
                  </span>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={config.monthlyBudget}
                    onChange={(event) =>
                      setConfig({
                        ...config,
                        monthlyBudget: Number(event.target.value) || 0
                      })
                    }
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                  />
                  <span className="mt-1 block text-[11px] text-gray-500">
                    Leave at 0 to turn budget tracking off. This warns you — it
                    does not stop sending.
                  </span>
                </label>

                {config.monthlyBudget > 0 && (
                  <label className="mt-2 block">
                    <span className="text-xs font-medium text-gray-700">
                      Warn at {config.budgetAlertThreshold}% of budget
                    </span>
                    <input
                      type="range"
                      min={1}
                      max={100}
                      value={config.budgetAlertThreshold}
                      onChange={(event) =>
                        setConfig({
                          ...config,
                          budgetAlertThreshold: Number(event.target.value)
                        })
                      }
                      className="mt-1 w-full accent-primary-600"
                    />
                  </label>
                )}
              </div>

              {error && <p className="text-xs text-danger-600">{error}</p>}
            </>
          )}
        </div>

        <div className="flex gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !config}
            className="flex-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingRatesDialog;
